import { useState, useCallback, useMemo, useEffect } from 'react';
import { SpreadsheetData, Row, Cell, ColumnFilter, SortState, CellHistoryEntry } from '../types/spreadsheet';
import * as API from '../../../../api';

export function useSpreadsheetData() {
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>({});
  const [activeFilters, setActiveFilters] = useState<{ [columnName: string]: string }>({});
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const generateColumnFilters = useCallback((rowsData: Row[]) => {
    const filters: ColumnFilter = {};
    
    columns.forEach(column => {
      const uniqueValues = new Set<string>();
      rowsData.forEach(row => {
        const cell = row[column] as Cell;
        const value = cell?.value?.trim();
        if (value) {
          uniqueValues.add(value);
        }
      });
      filters[column] = Array.from(uniqueValues).sort();
    });
    
    setColumnFilters(filters);
  }, [columns]);

  const saveDataToAPI = useCallback(async (updatedRows: Row[]) => {
    setIsSaving(true);
    
    try {
      // Converter dados para o formato esperado pela API
      const exportData: SpreadsheetData = {
        DataTransfer: columns.map(columnName => ({
          columnName,
          data: updatedRows.map(row => {
            const cell = row[columnName] as Cell;
            return cell?.value || '';
          })
        }))
      };
      const response:any = await API.updateTabelasDescricao({ data: JSON.stringify(exportData) })

      if (!response.data) {
        throw new Error(`Erro ao salvar: ${response.status} ${response.statusText}`);
      }

      const result = await response.data;
      
      if (!result.success) {
        throw new Error('Erro ao salvar: ' + (result.message || 'Erro desconhecido'));
      }

      console.log('Dados salvos com sucesso:', result);
      
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
    } finally {
      setIsSaving(false);
    }
  }, [columns]);

  const loadDataFromAPI = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response:any = await API.getTabelasDescricao();
      
      if (!response.data) {
        throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
      }
      
      const apiResponse = response.data;
      
      
      if (!apiResponse.success) {
        throw new Error('API retornou erro: ' + (apiResponse.message || 'Erro desconhecido'));
      }
      
      const data = JSON.parse(apiResponse.data);
      
      loadDataFromJSON(data);
      
    } catch (error) {
      console.error('Erro ao carregar dados da API:', error);
      setError(error instanceof Error ? error.message : 'Erro desconhecido ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Carregar dados automaticamente quando o componente é montado
  useEffect(() => {
    loadDataFromAPI();
  }, [loadDataFromAPI]);

  const loadDataFromJSON = useCallback((data: SpreadsheetData) => {
    const columnNames = data.DataTransfer.map(col => col.columnName);
    setColumns(columnNames);

    const maxRows = Math.max(...data.DataTransfer.map(col => col.data.length));
    
    const newRows: Row[] = [];
    for (let i = 0; i < maxRows; i++) {
      const rowId = crypto.randomUUID(); // Gerar GUID único para cada linha
      console.log('Loading row with ID:', rowId); // Debug log
      const row: Row = {
        id: rowId
      };
      columnNames.forEach((colName, colIndex) => {
        row[colName] = {
          value: data.DataTransfer[colIndex].data[i] || '',
          isEditing: false,
          history: [] // Inicializar histórico vazio
        };
      });
      newRows.push(row);
    }
    
    setRows(newRows);
    
    setTimeout(() => {
      const filters: ColumnFilter = {};
      columnNames.forEach(column => {
        const uniqueValues = new Set<string>();
        newRows.forEach(row => {
          const cell = row[column] as Cell;
          const value = cell?.value?.trim();
          if (value) {
            uniqueValues.add(value);
          }
        });
        filters[column] = Array.from(uniqueValues).sort();
      });
      setColumnFilters(filters);
    }, 0);
    
    setActiveFilters({});
    setSortState({ column: null, direction: null });
    setError(null);
  }, []);

  const updateCellValue = useCallback((rowIndex: number, columnName: string, newValue: string) => {
    setRows(prevRows => {
      const updatedRows = prevRows.map((row, index) => {
        if (index === rowIndex) {
          const currentCell = row[columnName] as Cell;
          const oldValue = currentCell?.value || '';
          
          // Adicionar valor antigo ao histórico se for diferente do novo
          if (oldValue !== newValue && oldValue !== '') {
            const historyEntry: CellHistoryEntry = {
              value: oldValue,
              timestamp: new Date().toISOString()
            };
            
            const updatedHistory = [...(currentCell?.history || []), historyEntry];
            
            return {
              ...row,
              [columnName]: {
                ...currentCell,
                value: newValue,
                history: updatedHistory
              }
            };
          } else {
            return {
              ...row,
              [columnName]: {
                ...currentCell,
                value: newValue
              }
            };
          }
        }
        return row;
      });
      
      // Salvar dados na API após a atualização
      setTimeout(() => {
        generateColumnFilters(updatedRows);
        saveDataToAPI(updatedRows);
      }, 0);
      
      return updatedRows;
    });
  }, [generateColumnFilters, saveDataToAPI]);

  const startEditing = useCallback((rowIndex: number, columnName: string) => {
    setRows(prevRows => 
      prevRows.map((row, index) => 
        index === rowIndex 
          ? { 
              ...row, 
              [columnName]: { 
                ...(row[columnName] as Cell), 
                isEditing: true 
              } 
            }
          : row
      )
    );
  }, []);

  const stopEditing = useCallback((rowIndex: number, columnName: string) => {
    setRows(prevRows => 
      prevRows.map((row, index) => 
        index === rowIndex 
          ? { 
              ...row, 
              [columnName]: { 
                ...(row[columnName] as Cell), 
                isEditing: false 
              } 
            }
          : row
      )
    );
  }, []);

  const addNewRow = useCallback(() => {
    const newRowId = crypto.randomUUID(); // Gerar GUID único para nova linha
    console.log('Adding new row with ID:', newRowId); // Debug log
    const newRow: Row = {
      GUID: {
        value: newRowId, 
        isEditing: false, 
        history: [] // Inicializar histórico vazio
      },
      id: newRowId
    };
    columns.forEach(col => {
      if (col == 'GUID') return;
      newRow[col] = { 
        value: '', 
        isEditing: false, 
        history: [] // Inicializar histórico vazio
      };
    });
    setRows(prevRows => {
      const updatedRows = [...prevRows, newRow];
      setTimeout(() => {
        generateColumnFilters(updatedRows);
        saveDataToAPI(updatedRows);
      }, 0);
      return updatedRows;
    });
  }, [columns, generateColumnFilters, saveDataToAPI]);

  const insertNewRow = useCallback((index: number) => {
    const newRowId = crypto.randomUUID(); // Gerar GUID único para nova linha
    console.log('Inserting new row with ID:', newRowId, 'at index:', index); // Debug log
    const newRow: Row = {
      id: newRowId
    };
    columns.forEach(col => {
      newRow[col] = { 
        value: '', 
        isEditing: false, 
        history: [] // Inicializar histórico vazio
      };
    });
    setRows(prevRows => {
      const updatedRows = [...prevRows];
      updatedRows.splice(index, 0, newRow);
      setTimeout(() => {
        generateColumnFilters(updatedRows);
        saveDataToAPI(updatedRows);
      }, 0);
      return updatedRows;
    });
  }, [columns, generateColumnFilters, saveDataToAPI]);

  const deleteRow = useCallback((rowIndex: number) => {
    setRows(prevRows => {
      const updatedRows = prevRows.filter((_, index) => index !== rowIndex);
      setTimeout(() => {
        generateColumnFilters(updatedRows);
        saveDataToAPI(updatedRows);
      }, 0);
      return updatedRows;
    });
  }, [generateColumnFilters, saveDataToAPI]);

  const handleSort = useCallback((columnName: string) => {
    setSortState(prevSort => {
      if (prevSort.column === columnName) {
        // Cycle through: asc -> desc -> null
        const newDirection = prevSort.direction === 'asc' ? 'desc' : 
                           prevSort.direction === 'desc' ? null : 'asc';
        return { column: newDirection ? columnName : null, direction: newDirection };
      } else {
        return { column: columnName, direction: 'asc' };
      }
    });
  }, []);

  const handleFilterChange = useCallback((columnName: string, value: string) => {
    setActiveFilters(prev => ({
      ...prev,
      [columnName]: value
    }));
  }, []);

  const clearFilter = useCallback((columnName: string) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnName];
      return newFilters;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  const getFilteredAndSortedRows = useCallback((searchTerm: string) => {
    let filtered = rows.filter(row => {
      const matchesSearch = !searchTerm || 
        Object.entries(row).some(([key, value]) => {
          if (key === 'id') return false; // Não buscar pelo GUID
          const cell = value as Cell;
          return cell?.value?.toLowerCase().includes(searchTerm.toLowerCase());
        });

      const matchesColumnFilters = Object.entries(activeFilters).every(([columnName, filterValue]) => {
        if (!filterValue) return true;
        const cell = row[columnName] as Cell;
        return cell?.value === filterValue;
      });

      return matchesSearch && matchesColumnFilters;
    });

    // Apply sorting
    if (sortState.column && sortState.direction) {
      filtered = [...filtered].sort((a, b) => {
        const aCell = a[sortState.column!] as Cell;
        const bCell = b[sortState.column!] as Cell;
        const aValue = aCell?.value || '';
        const bValue = bCell?.value || '';
        
        const comparison = aValue.localeCompare(bValue, 'pt-BR', { 
          numeric: true, 
          sensitivity: 'base' 
        });
        
        return sortState.direction === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [rows, activeFilters, sortState]);

  return {
    columns,
    rows,
    columnFilters,
    activeFilters,
    sortState,
    isLoading,
    isSaving,
    error,
    loadDataFromJSON,
    loadDataFromAPI,
    updateCellValue,
    startEditing,
    stopEditing,
    addNewRow,
    insertNewRow,
    deleteRow,
    handleSort,
    handleFilterChange,
    clearFilter,
    clearAllFilters,
    getFilteredAndSortedRows
  };
}